import { ref, watch, type Ref } from 'vue';

const THUMB_W = 160;

/**
 * Lazily extracts evenly-spaced thumbnail frames from a video source URL.
 *
 * Extraction is performed in a hidden video element (no interference with the
 * main player) and triggered explicitly via `triggerExtraction()`.  Call that
 * once on the first seek-bar interaction; subsequent calls are no-ops.
 *
 * @param srcRef      Reactive video URL.  Changing it resets all state so a
 *                    fresh extraction can be triggered for the new source.
 * @param totalFrames Number of frames to sample (default 20).
 */
export function useVideoPreviewFrames(srcRef: Ref<string>, totalFrames = 20) {
  const frames = ref<string[]>([]);
  const isExtracting = ref(false);
  const isReady = ref(false);

  // Non-reactive: these only need to guard the async loop, not drive the UI.
  let extractedDuration = 0;
  let triggered = false;
  let activeSrc = '';

  watch(srcRef, () => {
    frames.value = [];
    isReady.value = false;
    isExtracting.value = false;
    extractedDuration = 0;
    triggered = false;
    activeSrc = '';
  }, { flush: 'sync' });

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  function seekAndCapture(
    video: HTMLVideoElement,
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    time: number,
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      function onSeeked() {
        video.removeEventListener('seeked', onSeeked);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        resolve();
      }
      video.addEventListener('seeked', onSeeked);
      video.currentTime = time;
    });
  }

  async function doExtract(src: string): Promise<void> {
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'metadata';
    video.src = src;

    try {
      await new Promise<void>((resolve, reject) => {
        video.addEventListener('loadedmetadata', () => resolve(), { once: true });
        video.addEventListener('error', () => reject(new Error('video load error')), { once: true });
        video.load();
      });
    } catch {
      if (activeSrc === src) isExtracting.value = false;
      return;
    }

    if (activeSrc !== src) return; // source changed while loading

    const duration = video.duration;
    if (!isFinite(duration) || duration <= 0) {
      isExtracting.value = false;
      return;
    }

    extractedDuration = duration;

    const thumbH =
      video.videoWidth > 0
        ? Math.round((THUMB_W * video.videoHeight) / video.videoWidth)
        : 90;
    const canvas = document.createElement('canvas');
    canvas.width = THUMB_W;
    canvas.height = thumbH;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      isExtracting.value = false;
      return;
    }

    const result: string[] = [];
    for (let i = 0; i < totalFrames; i++) {
      if (activeSrc !== src) break; // source changed mid-extraction
      const time =
        totalFrames === 1 ? 0 : (i / (totalFrames - 1)) * duration;
      await seekAndCapture(video, canvas, ctx, time);
      result.push(canvas.toDataURL('image/jpeg', 0.6));
    }

    video.src = ''; // release the media resource

    if (activeSrc === src) {
      frames.value = result;
      isReady.value = true;
      isExtracting.value = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /** Start frame extraction.  Safe to call repeatedly; only runs once per src. */
  function triggerExtraction(): void {
    if (triggered || !srcRef.value) return;
    triggered = true;
    activeSrc = srcRef.value;
    isExtracting.value = true;
    doExtract(activeSrc).catch(() => {
      if (activeSrc) isExtracting.value = false;
    });
  }

  /** Return the data-URL of the pre-extracted frame closest to `time` seconds. */
  function getFrameAtTime(time: number): string {
    if (!frames.value.length || extractedDuration <= 0) return '';
    const idx = Math.round(
      (time / extractedDuration) * (frames.value.length - 1),
    );
    return frames.value[Math.min(Math.max(0, idx), frames.value.length - 1)];
  }

  return {
 frames,
isExtracting,
isReady,
triggerExtraction,
getFrameAtTime,
};
}
